import {
  createQuery,
  keepPreviousData,
  useQueryClient,
} from "@tanstack/solid-query";
import {
  createFileRoute,
  ErrorComponent,
  useNavigate,
} from "@tanstack/solid-router";
import type { JSX } from "solid-js";
import { createSignal, lazy, onMount, Show, Suspense } from "solid-js";
import { toast } from "solid-sonner";
import { z } from "zod";
import {
  IconArrowRightFromLine,
  IconFileText,
  IconImport,
  IconInfo,
  IconLink,
  IconPencil,
  IconSheet,
  IconTrash,
} from "~/components/icons";
import { NotFound } from "~/components/not-found";
import { StandardRouteAssociationsPanel } from "~/components/standard-route/detail/associations-panel";
import { StandardRouteDetailCard } from "~/components/standard-route/detail/detail-card";
import { StandardRouteDetailHeader } from "~/components/standard-route/detail/header";
import {
  StandardRouteDetailGrid,
  StandardRouteDetailLeftColumn,
  StandardRouteDetailMode,
  StandardRouteDetailRightCard,
  StandardRouteDetailRightCardHeader,
  StandardRouteDetailRightColumn,
  StandardRouteDetailTabs,
  StandardRouteDetailTabsContent,
  StandardRouteDetailTabsStack,
} from "~/components/standard-route/detail/layout";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { DatePickerField } from "~/components/ui/date-picker-field";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "~/components/ui/dropdown-menu";
import { Separator } from "~/components/ui/separator";
import { TabsList, TabsTrigger } from "~/components/ui/tabs";
import {
  TextField,
  TextFieldInput,
  TextFieldLabel,
} from "~/components/ui/text-field";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { prefetchAssociations } from "~/features/association/association.prefetch";
import { businessesResource } from "~/features/business/business.resource";
import { getWorksheetPageData } from "~/features/worksheets/worksheet-page";
import {
  createWorksheets,
  deleteWorksheets,
  updateWorksheets,
} from "~/features/worksheets/worksheets.api";
import { WorksheetDetailResource } from "~/features/worksheets/worksheets.resource";
import type { WorksheetsItem } from "~/features/worksheets/worksheets.server";
import {
  addListItemToCache,
  updateListItemInCache,
} from "~/lib/optimisticCache";
import { withBusinessGuard } from "~/lib/route-guards";
import { exportToExcel, importFromExcel } from "~/lib/spreadsheet-export";
import { formatDisplayCalendarDate, formatDisplayDate } from "~/lib/utils";

const LIMIT = 20;

const Spreadsheet = lazy(() => import("~/components/spreadsheet/spreadsheet"));

export const Route = createFileRoute(
  "/_dashboard/dashboard/worksheets/$worksheetId",
)({
  staticData: {
    breadcrumb: (m) => m.loaderData?.worksheet?.name ?? "Worksheet",
  },
  validateSearch: z.object({
    mode: z.enum(["edit"]).optional(),
  }),
  // Route-specific 404 message (takes precedence over the dashboard-wide
  // notFoundComponent). Shown when a worksheet doesn't exist — e.g. it was
  // deleted, or an AI agent linked here by mistake.
  notFoundComponent: () => (
    <NotFound
      title="Worksheet not found"
      message="This worksheet doesn't exist. It may have been deleted, or an agent may have linked you here by mistake."
      homeTo="/dashboard/worksheets"
      homeLabel="Go to worksheets"
    />
  ),
  ...withBusinessGuard(),
  loader: async ({ params, context }) => {
    try {
      const collection = await context.queryClient.ensureQueryData(
        businessesResource.options(undefined),
      );
      const businessId = collection.activeBusinessId!;

      if (params.worksheetId === "new") {
        const data = await getWorksheetPageData({
          data: { businessId, worksheetId: "new" },
        });
        context.queryClient.setQueryData(
          WorksheetDetailResource.key({
            businessId,
            id: params.worksheetId,
          }),
          null,
        );
        return { businessId, worksheet: null };
      }

      const data = await getWorksheetPageData({
        data: { businessId, worksheetId: params.worksheetId },
      });
      if (data.worksheet) {
        WorksheetDetailResource.seed(
          context.queryClient,
          { businessId, id: params.worksheetId },
          data.worksheet,
        );
      }
      // Prefetch the Associations panel queries so they resolve from cache on
      // mount instead of flashing loading states. Fire-and-forget (not awaited)
      // so it never blocks the loader's critical path or SSR.
      prefetchAssociations(
        context.queryClient,
        businessId,
        "worksheet",
        params.worksheetId,
      );
      return { businessId, worksheet: data.worksheet };
    } catch (error) {
      console.error("Loader error:", error);
      throw error;
    }
  },
  errorComponent: ({ error }) => <ErrorComponent error={error} />,
  component: WorksheetDetailPage,
});

type Mode = "view" | "edit";

type WorksheetFormState = {
  name: string;
  description: string;
  type: string;
  date: string;
  data: { sheets: any[] };
};

function cloneSheetsData(value: unknown): any[] {
  if (!Array.isArray(value)) return [{ name: "Sheet1" }];
  try {
    if (typeof structuredClone === "function") {
      return structuredClone(value);
    }
  } catch {
    // Fall through to JSON clone for non-structured-cloneable values.
  }
  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return [{ name: "Sheet1" }];
  }
}

function toDateInputValue(value: Date | string | null | undefined): string {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
}

function WorksheetDetailPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const params = Route.useParams();
  const data = Route.useLoaderData();

  const isNew = () => params().worksheetId === "new";
  const search = Route.useSearch();
  const initialMode: Mode = search().mode === "edit" ? "edit" : "view";
  const [mode, setMode] = createSignal<Mode>(initialMode);

  onMount(() => {
    if (initialMode === "edit") {
      const url = new URL(window.location.href);
      url.searchParams.delete("mode");
      window.history.replaceState(window.history.state, "", url.toString());
    }
  });

  const loaderWorksheet = data().worksheet;
  onMount(() => {
    if (!loaderWorksheet) return;
    WorksheetDetailResource.seedIfEmpty(
      qc,
      {
        businessId: data().businessId,
        id: params().worksheetId,
      },
      loaderWorksheet,
    );
  });

  // Seed null into QueryClient for "new" to prevent SSR deferred stream hang
  if (isNew()) {
    const newKey = WorksheetDetailResource.key({
      businessId: data().businessId,
      id: params().worksheetId,
    });
    if (qc.getQueryData(newKey) === undefined) {
      qc.setQueryData(newKey, null);
    }
  }

  const worksheetQuery = createQuery(() => ({
    ...WorksheetDetailResource.options(
      { businessId: data().businessId, id: params().worksheetId },
      !isNew(),
    ),
    placeholderData: keepPreviousData,
  }));

  return (
    <div
      class={`flex flex-1 flex-col px-4 sm:px-6 pb-3 pt-10 ${
        !isNew() && mode() === "view"
          ? "@5xl/main:h-[calc(100svh-4.5rem)] @5xl/main:max-h-[calc(100svh-4.5rem)] @5xl/main:min-h-0 @5xl/main:overflow-auto"
          : ""
      }`}
    >
      <Show when={isNew()}>
        <WorksheetEdit
          worksheet={null}
          businessId={data().businessId}
          onCancel={() => navigate({ to: "/dashboard/worksheets" })}
          onSaved={(saved) => {
            navigate({
              to: "/dashboard/worksheets/$worksheetId",
              params: { worksheetId: saved.id },
            });
          }}
        />
      </Show>
      <Show when={!isNew()}>
        <Show
          when={worksheetQuery.data}
          fallback={
            <div role="status" class="w-full animate-pulse">
              <div class="h-150 bg-muted rounded-lg w-full" />
            </div>
          }
        >
          {(worksheet) => (
            <Show
              when={mode() === "edit"}
              fallback={
                <WorksheetView
                  worksheet={worksheet()}
                  businessId={data().businessId}
                  onEdit={() => setMode("edit")}
                  onDeleted={() => {
                    WorksheetDetailResource.remove(qc, {
                      businessId: data().businessId,
                      id: params().worksheetId,
                    });
                    // Remove the list cache so the table doesn't flash the
                    // deleted row before a background refetch.
                    qc.removeQueries({
                      queryKey: ["worksheet", data().businessId],
                    });
                    navigate({ to: "/dashboard/worksheets" });
                  }}
                />
              }
            >
              <WorksheetEdit
                worksheet={worksheet()}
                businessId={data().businessId}
                onCancel={() => setMode("view")}
                onSaved={() => setMode("view")}
              />
            </Show>
          )}
        </Show>
      </Show>
    </div>
  );
}

function WorksheetEdit(props: {
  worksheet: WorksheetsItem | null;
  businessId: string;
  onCancel: () => void;
  onSaved: (saved: { id: string }) => void;
}) {
  const qc = useQueryClient();
  const [isSaving, setIsSaving] = createSignal(false);
  const [isMounted, setIsMounted] = createSignal(false);
  let spreadsheetRef: {
    exportData: () => any;
    loadData: (data: any) => void;
  } | null = null;
  let fileInputRef: HTMLInputElement | undefined;

  onMount(() => setIsMounted(true));

  const [form, setForm] = createSignal<WorksheetFormState>({
    name: props.worksheet?.name ?? "",
    description: props.worksheet?.description ?? "",
    type: props.worksheet?.type ?? "",
    date: toDateInputValue(props.worksheet?.date),
    data: {
      sheets:
        props.worksheet?.data &&
        typeof props.worksheet.data === "object" &&
        "sheets" in props.worksheet.data
          ? cloneSheetsData((props.worksheet.data as { sheets: any[] }).sheets)
          : [{ name: "Sheet1" }],
    },
  });

  const save = async () => {
    const payload = {
      name: form().name.trim(),
      description: form().description.trim() || null,
      type: form().type.trim() || null,
      date: form().date ? new Date(form().date) : null,
      data: form().data,
    };

    if (!payload.name) {
      toast.error("Name is required");
      return;
    }

    setIsSaving(true);
    try {
      if (!props.worksheet) {
        const created = await createWorksheets({
          data: {
            businessId: props.businessId,
            item: payload,
          },
        });
        // Optimistically add the new item so it appears immediately on
        // navigation back. A background refetch corrects ordering.
        addListItemToCache<WorksheetsItem>(
          qc,
          ["worksheet", props.businessId],
          created,
        );
        toast.success("Worksheet created");
        props.onSaved({ id: created.id });
        return;
      }

      const updated = await updateWorksheets({
        data: {
          businessId: props.businessId,
          id: props.worksheet.id,
          updates: payload,
        },
      });

      WorksheetDetailResource.seed(
        qc,
        {
          businessId: props.businessId,
          id: props.worksheet.id,
        },
        updated,
      );

      // Optimistically update every worksheet list query variant so the table
      // shows the new values immediately on navigation back — even if the route
      // loader re-seeds stale data.
      updateListItemInCache<WorksheetsItem>(
        qc,
        ["worksheet", props.businessId],
        props.worksheet.id,
        updated,
      );

      toast.success("Worksheet updated");
      props.onSaved({ id: props.worksheet.id });
    } catch (error) {
      toast.error("Failed to save worksheet", {
        description: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleExport = async () => {
    if (!spreadsheetRef) return;
    const sheets = spreadsheetRef.exportData();
    await exportToExcel(sheets, `${form().name || "worksheet"}.xlsx`);
    toast.success("Exported worksheet");
  };

  const handleImport = async (event: Event) => {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    try {
      const sheets = await importFromExcel(file);
      const nextSheets = cloneSheetsData(sheets);
      setForm((prev) => ({
        ...prev,
        data: { sheets: nextSheets },
      }));
      spreadsheetRef?.loadData(nextSheets);
      toast.success("Imported spreadsheet");
    } catch (error) {
      console.error("Spreadsheet import error:", error);
      toast.error("Failed to import spreadsheet", {
        description: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      input.value = "";
    }
  };

  return (
    <Card class="-mx-4 sm:mx-0 rounded-none sm:rounded-lg border-x-0 sm:border-x shadow-none sm:shadow-sm flex flex-1 flex-col @5xl/main:min-h-0 @5xl/main:overflow-hidden">
      <CardHeader class="px-4 sm:px-6 pt-6 pb-0">
        <CardTitle class="text-base">
          {props.worksheet ? "Edit Worksheet" : "Add Worksheet"}
        </CardTitle>
        <p class="mt-0.5 text-sm text-muted-foreground">
          {props.worksheet
            ? "Update the worksheet details and spreadsheet. Changes apply immediately on save."
            : "Create a new worksheet with a spreadsheet."}
        </p>
      </CardHeader>
      <CardContent class="flex flex-1 flex-col gap-5 pt-6 @5xl/main:min-h-0 @5xl/main:overflow-hidden">
        <div class="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
          <div class="lg:col-span-2">
            <TextField>
              <TextFieldLabel>Name</TextFieldLabel>
              <TextFieldInput
                value={form().name}
                onInput={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    name: e.currentTarget.value,
                  }))
                }
                placeholder="e.g., Monthly Analysis"
              />
            </TextField>
          </div>
          <TextField>
            <TextFieldLabel>Type</TextFieldLabel>
            <TextFieldInput
              value={form().type}
              onInput={(e) =>
                setForm((prev) => ({
                  ...prev,
                  type: e.currentTarget.value,
                }))
              }
              placeholder="journal-entry, analysis, etc."
            />
          </TextField>
          <DatePickerField
            label="Date"
            value={form().date || ""}
            onChange={(value) => {
              setForm((prev) => ({
                ...prev,
                date: value ? String(value) : "",
              }));
            }}
          />
          <div class="md:col-span-2 lg:col-span-4">
            <TextField>
              <TextFieldLabel>Description</TextFieldLabel>
              <TextFieldInput
                value={form().description}
                onInput={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    description: e.currentTarget.value,
                  }))
                }
                placeholder="Worksheet description"
              />
            </TextField>
          </div>
        </div>

        <Separator />

        <div class="flex flex-wrap items-center justify-between gap-3">
          <CardTitle class="text-base">Spreadsheet</CardTitle>
          <div class="flex items-center gap-2">
            <input
              type="file"
              ref={fileInputRef}
              accept=".xlsx,.xls"
              class="hidden"
              onChange={(e) => void handleImport(e)}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInputRef?.click()}
              disabled={isSaving()}
            >
              <IconImport class="size-4" />
              Import Excel
            </Button>
            <Button variant="outline" size="sm" onClick={handleExport}>
              <IconArrowRightFromLine class="size-4" />
              Export Excel
            </Button>
          </div>
        </div>

        <div class="min-h-112 @5xl/main:flex-1 @5xl/main:min-h-136 @5xl/main:overflow-hidden">
          <Show when={isMounted()}>
            <Suspense
              fallback={
                <div class="h-130 w-full rounded-md border border-border bg-muted/20" />
              }
            >
              <Spreadsheet
                initialData={cloneSheetsData(form().data.sheets || [])}
                mode="edit"
                class="h-full min-h-112 @5xl/main:min-h-136"
                onChange={(sheets) =>
                  setForm((prev) => ({
                    ...prev,
                    data: { sheets: cloneSheetsData(sheets) },
                  }))
                }
                onApiReady={(api) => {
                  spreadsheetRef = api;
                }}
              />
            </Suspense>
          </Show>
        </div>
      </CardContent>
      <Separator />
      <CardFooter class="flex items-center justify-end gap-2 px-4 sm:px-6 pt-5 pb-6">
        <Button
          variant="outline"
          onClick={props.onCancel}
          disabled={isSaving()}
        >
          Cancel
        </Button>
        <Button onClick={() => void save()} disabled={isSaving()}>
          {isSaving() ? "Saving..." : "Save"}
        </Button>
      </CardFooter>
    </Card>
  );
}

function WorksheetView(props: {
  worksheet: WorksheetsItem;
  businessId: string;
  onEdit: () => void;
  onDeleted: () => void;
}) {
  const [showDeleteDialog, setShowDeleteDialog] = createSignal(false);
  const [isMounted, setIsMounted] = createSignal(false);

  onMount(() => setIsMounted(true));

  const handleExport = async () => {
    const sheets =
      props.worksheet.data &&
      typeof props.worksheet.data === "object" &&
      "sheets" in props.worksheet.data
        ? cloneSheetsData((props.worksheet.data as { sheets: any[] }).sheets)
        : [{ name: "Sheet1" }];
    await exportToExcel(sheets, `${props.worksheet.name || "worksheet"}.xlsx`);
    toast.success("Exported worksheet");
  };

  return (
    <div class="flex flex-1 flex-col gap-6">
      <StandardRouteDetailHeader
        icon={<IconSheet class="size-5" />}
        title={props.worksheet.name || "Worksheet"}
        badge={props.worksheet.type ?? undefined}
        date={props.worksheet.date ?? undefined}
        menuContent={
          <>
            <DropdownMenuItem onClick={props.onEdit}>
              <IconPencil class="size-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleExport}>
              <IconArrowRightFromLine class="size-4" />
              Export Excel
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              class="text-destructive focus:text-destructive"
              onClick={() => setShowDeleteDialog(true)}
            >
              <IconTrash class="size-4" />
              Delete
            </DropdownMenuItem>
          </>
        }
      />

      <StandardRouteDetailMode mode="content">
        <StandardRouteDetailGrid>
          <StandardRouteDetailLeftColumn>
            <div class="space-y-6">
              <StandardRouteDetailCard
                icon={<IconFileText class="size-5" />}
                title="Worksheet"
                contentClass="divide-y divide-border"
                action={
                  <Tooltip>
                    <TooltipTrigger
                      as={Button<"button">}
                      variant="ghost"
                      size="icon"
                      class="size-7 text-muted-foreground hover:text-foreground"
                      aria-label="Edit worksheet"
                      onClick={props.onEdit}
                    >
                      <IconPencil class="size-4" />
                    </TooltipTrigger>
                    <TooltipContent>Edit</TooltipContent>
                  </Tooltip>
                }
              >
                <InfoRow label="Name" value={props.worksheet.name} />
                <InfoRow
                  label="Date"
                  value={formatDisplayCalendarDate(props.worksheet.date)}
                />
                <InfoRow label="Type" value={props.worksheet.type} />
                <Show when={props.worksheet.description}>
                  <div class="py-3.5">
                    <p class="mb-2 text-sm text-muted-foreground">
                      Description
                    </p>
                    <p class="text-sm">{props.worksheet.description}</p>
                  </div>
                </Show>
              </StandardRouteDetailCard>
            </div>
          </StandardRouteDetailLeftColumn>

          <StandardRouteDetailRightColumn>
            <StandardRouteDetailRightCard>
              <StandardRouteDetailRightCardHeader class="pb-0">
                <StandardRouteDetailTabs
                  defaultValue="spreadsheet"
                  class="flex flex-col"
                >
                  <TabsList class="w-full">
                    <TabsTrigger value="spreadsheet" class="flex-1">
                      <IconSheet class="mr-2 size-4" />
                      <span class="hidden sm:inline">Spreadsheet</span>
                    </TabsTrigger>
                    <TabsTrigger value="metadata" class="flex-1">
                      <IconInfo class="mr-2 size-4" />
                      <span class="hidden sm:inline">Metadata</span>
                    </TabsTrigger>
                    <TabsTrigger value="associations" class="flex-1">
                      <IconLink class="mr-2 size-4" />
                      <span class="hidden sm:inline">Associations</span>
                    </TabsTrigger>
                  </TabsList>

                  <StandardRouteDetailTabsStack>
                    <StandardRouteDetailTabsContent
                      value="spreadsheet"
                      class="overflow-hidden"
                      forceMount
                    >
                      <CardContent class="px-0 pb-6">
                        <Show
                          when={isMounted()}
                          fallback={
                            <div class="h-150 w-full rounded-md border border-border bg-muted/20" />
                          }
                        >
                          <Suspense
                            fallback={
                              <div class="h-150 w-full rounded-md border border-border bg-muted/20" />
                            }
                          >
                            <Spreadsheet
                              initialData={
                                props.worksheet.data &&
                                typeof props.worksheet.data === "object" &&
                                "sheets" in props.worksheet.data
                                  ? cloneSheetsData(
                                      (
                                        props.worksheet.data as {
                                          sheets: any[];
                                        }
                                      ).sheets,
                                    )
                                  : [{ name: "Sheet1" }]
                              }
                              mode="view"
                              class="h-150"
                              onChange={() => undefined}
                            />
                          </Suspense>
                        </Show>
                      </CardContent>
                    </StandardRouteDetailTabsContent>

                    <StandardRouteDetailTabsContent
                      value="associations"
                      class="overflow-auto"
                      forceMount
                    >
                      <CardContent class="px-0">
                        <StandardRouteAssociationsPanel
                          source={{ type: "worksheet", id: props.worksheet.id }}
                          businessId={props.businessId}
                          tabs={[
                            {
                              type: "bank-account",
                              defaultVisibility: {
                                bankName: false,
                                accountCode: false,
                                description: false,
                                updatedAt: false,
                              },
                            },
                            {
                              type: "bank-transaction",
                              defaultVisibility: {
                                status: false,
                                institutionName: false,
                                mappedAccountCode: false,
                                bankAccountName: false,
                                updatedAt: false,
                              },
                            },
                            {
                              type: "journal-entry",
                              defaultVisibility: {
                                reference: false,
                                entries: false,
                              },
                            },
                            {
                              type: "document",
                              defaultVisibility: { description: false },
                            },
                            {
                              type: "fixed-asset",
                              defaultVisibility: {
                                purchaseDate: false,
                                depreciationMethod: false,
                                purchaseCost: false,
                                updatedAt: false,
                              },
                            },
                            {
                              type: "vendor",
                              defaultVisibility: {
                                address: false,
                                website: false,
                                contactName: false,
                              },
                            },
                          ]}
                        />
                      </CardContent>
                    </StandardRouteDetailTabsContent>

                    <StandardRouteDetailTabsContent
                      value="metadata"
                      class="overflow-auto"
                      forceMount
                    >
                      <CardContent class="px-0">
                        <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
                          <TextField>
                            <TextFieldInput
                              label="Created"
                              value={
                                formatDisplayDate(props.worksheet.createdAt) ??
                                "N/A"
                              }
                              disabled
                            />
                          </TextField>
                          <TextField>
                            <TextFieldInput
                              label="Last Modified"
                              value={
                                formatDisplayDate(props.worksheet.updatedAt) ??
                                "N/A"
                              }
                              disabled
                            />
                          </TextField>
                          <div class="md:col-span-2">
                            <TextField>
                              <TextFieldInput
                                label="Worksheet ID"
                                value={props.worksheet.id}
                                disabled
                              />
                            </TextField>
                          </div>
                        </div>
                      </CardContent>
                    </StandardRouteDetailTabsContent>
                  </StandardRouteDetailTabsStack>
                </StandardRouteDetailTabs>
              </StandardRouteDetailRightCardHeader>
            </StandardRouteDetailRightCard>
          </StandardRouteDetailRightColumn>
        </StandardRouteDetailGrid>
      </StandardRouteDetailMode>

      <DeleteWorksheetDialog
        worksheet={props.worksheet}
        businessId={props.businessId}
        open={showDeleteDialog()}
        onOpenChange={setShowDeleteDialog}
        onDeleted={props.onDeleted}
      />
    </div>
  );
}

function DeleteWorksheetDialog(props: {
  worksheet: WorksheetsItem;
  businessId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleted: () => void;
}) {
  const [isDeleting, setIsDeleting] = createSignal(false);

  async function handleDelete() {
    setIsDeleting(true);
    try {
      await deleteWorksheets({
        data: {
          businessId: props.businessId,
          id: props.worksheet.id,
        },
      });
      toast.success("Worksheet deleted");
      props.onDeleted();
    } catch {
      toast.error("Failed to delete worksheet");
    } finally {
      setIsDeleting(false);
      props.onOpenChange(false);
    }
  }

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent class="max-w-md">
        <DialogHeader>
          <DialogTitle>Delete Worksheet</DialogTitle>
          <DialogDescription>
            This action cannot be undone and will permanently remove
            <span class="font-semibold"> "{props.worksheet.name}"</span>.
          </DialogDescription>
        </DialogHeader>
        <Separator />
        <DialogFooter>
          <Button variant="outline" onClick={() => props.onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={isDeleting()}
          >
            {isDeleting() ? "Deleting..." : "Delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InfoRow(props: {
  label: string;
  value: JSX.Element | string | null | undefined;
}) {
  return (
    <div class="flex items-center justify-between py-3.5">
      <p class="text-sm text-muted-foreground">{props.label}</p>
      <h6 class="text-sm font-semibold">{props.value || "-"}</h6>
    </div>
  );
}
